import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Package, Search, ChevronDown, ChevronUp, Trash2, X, Check,
  Clock, Mail, Phone, Building2, FileText, ArrowRight,
  AlertCircle, Truck, CheckCircle2, MessageSquare,
  Filter, ShoppingBag, HelpCircle, Flame, Star, CalendarClock, Download, RefreshCw
} from 'lucide-react';
import ShareExportMenu from './ShareExportMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { id: 'new', label: 'New', color: '#ef4444', bg: '#fef2f2' },
  { id: 'contacted', label: 'Contacted', color: '#f97316', bg: '#fff7ed' },
  { id: 'quoted', label: 'Quoted', color: '#eab308', bg: '#fefce8' },
  { id: 'confirmed', label: 'Confirmed', color: '#22c55e', bg: '#f0fdf4' },
  { id: 'shipped', label: 'Shipped', color: '#3b82f6', bg: '#eff6ff' },
];

const PRIORITIES = [
  { id: 'hot', label: 'Hot', color: '#dc2626', bg: '#fef2f2', icon: Flame },
  { id: 'warm', label: 'Warm', color: '#f97316', bg: '#fff7ed', icon: Flame },
  { id: 'cold', label: 'Cold', color: '#6b7280', bg: '#f3f4f6', icon: null },
  { id: 'vip', label: 'VIP', color: '#7c3aed', bg: '#f5f3ff', icon: Star },
];

const getPriorityConfig = (p) => PRIORITIES.find(pr => pr.id === p) || null;

const STATUS_ICONS = {
  new: AlertCircle, contacted: Mail, quoted: FileText,
  confirmed: CheckCircle2, shipped: Truck,
};

const TYPES = [
  { id: 'order', label: 'Camper Order', icon: ShoppingBag, color: '#E8520A', bg: '#FFF0E8' },
  { id: 'kei_truck_order', label: 'Kei Truck Order', icon: Truck, color: '#3b82f6', bg: '#eff6ff' },
  { id: 'contact', label: 'Contact', icon: MessageSquare, color: '#8b5cf6', bg: '#f5f3ff' },
];

const getTypeConfig = (type) => TYPES.find(t => t.id === type) || { label: type, icon: HelpCircle, color: '#6b7280', bg: '#f3f4f6' };

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

/* ── Export Helpers ── */
const exportCSV = (orders) => {
  const headers = ['Type', 'Name', 'Email', 'Phone', 'Company', 'Product', 'Status', 'Priority', 'Follow-up', 'Message', 'Date', 'Notes'];
  const rows = orders.map(o => [
    getTypeConfig(o.type).label,
    o.customer_name || '', o.customer_email || '', o.customer_phone || '',
    o.customer_company || '', o.product || '', o.status || '',
    o.priority || '', o.follow_up_date || '',
    (o.message || '').replace(/[\n\r,]/g, ' '),
    o.created_at ? new Date(o.created_at).toLocaleString() : '',
    (o.notes || '').replace(/[\n\r,]/g, ' '),
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `orders_contacts_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportPDF = (orders) => {
  const typeLabel = (t) => getTypeConfig(t).label;
  const html = `<!DOCTYPE html><html><head><title>Orders & Contacts Export</title>
<style>
  body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a}
  h1{font-size:20px;margin-bottom:4px}
  .sub{font-size:12px;color:#888;margin-bottom:24px}
  .card{border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin-bottom:12px;page-break-inside:avoid}
  .card-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
  .type-badge{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;padding:3px 8px;border-radius:6px}
  .status-badge{font-size:10px;font-weight:700;padding:3px 8px;border-radius:20px}
  .priority-badge{font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px;margin-left:6px}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  .field label{font-size:9px;font-weight:700;text-transform:uppercase;color:#888;letter-spacing:0.5px}
  .field p{font-size:13px;margin:2px 0 0}
  .msg{background:#f9fafb;border-radius:8px;padding:10px;margin-top:10px;font-size:13px}
  .notes{background:#fefce8;border-radius:8px;padding:10px;margin-top:8px;font-size:12px;color:#854d0e}
  .followup{background:#eff6ff;border-radius:8px;padding:8px 10px;margin-top:8px;font-size:12px;color:#1e40af}
  hr{border:none;border-top:1px solid #f3f4f6;margin:12px 0}
  @media print{body{margin:16px}.card{break-inside:avoid}}
</style></head><body>
<h1>Orders & Contacts</h1>
<p class="sub">Exported ${new Date().toLocaleString()} · ${orders.length} items</p>
${orders.map(o => {
  const tc = getTypeConfig(o.type);
  const st = STATUSES.find(s => s.id === o.status) || STATUSES[0];
  const pr = getPriorityConfig(o.priority);
  return `<div class="card">
    <div class="card-header">
      <div>
        <span class="type-badge" style="background:${tc.bg};color:${tc.color}">${typeLabel(o.type)}</span>
        <span class="status-badge" style="background:${st.bg};color:${st.color};margin-left:6px">${st.label}</span>
        ${pr ? `<span class="priority-badge" style="background:${pr.bg};color:${pr.color}">${pr.label}</span>` : ''}
      </div>
      <span style="font-size:11px;color:#888">${formatDate(o.created_at)} ${formatTime(o.created_at)}</span>
    </div>
    <div class="grid">
      <div class="field"><label>Name</label><p><strong>${o.customer_name || '—'}</strong></p></div>
      <div class="field"><label>Email</label><p>${o.customer_email || '—'}</p></div>
      <div class="field"><label>Phone</label><p>${o.customer_phone || '—'}</p></div>
      <div class="field"><label>Company</label><p>${o.customer_company || '—'}</p></div>
      ${o.product ? `<div class="field"><label>Product</label><p>${o.product}</p></div>` : ''}
      ${o.series ? `<div class="field"><label>Series</label><p>${o.series}</p></div>` : ''}
    </div>
    ${o.details && Object.keys(o.details).length > 0 ? `<hr/><div class="grid">${Object.entries(o.details).map(([k,v]) => `<div class="field"><label>${k.replace(/_/g,' ')}</label><p>${v}</p></div>`).join('')}</div>` : ''}
    ${o.message ? `<div class="msg">${o.message}</div>` : ''}
    ${o.follow_up_date ? `<div class="followup"><strong>Follow-up:</strong> ${o.follow_up_date}</div>` : ''}
    ${o.notes ? `<div class="notes"><strong>Notes:</strong> ${o.notes}</div>` : ''}
  </div>`;
}).join('')}
</body></html>`;
  const sanitized = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style', 'html', 'head', 'body', 'meta'], ADD_ATTR: ['charset'] });
  const blob = new Blob([sanitized], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (w) w.onload = () => { w.print(); URL.revokeObjectURL(url); };
};

const OrderManagementTab = ({ authHeaders, flash }) => {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const loadOrders = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/orders${params}`, { headers: authHeaders() });
      setOrders(res.data);
      // Auto-open order from URL param (e.g. from email link)
      const urlParams = new URLSearchParams(window.location.search);
      const orderId = urlParams.get('order');
      if (orderId && !selectedOrder) {
        const found = res.data.find(o => o.id === orderId);
        if (found) {
          setSelectedOrder(found);
          setEditNotes(found.notes || '');
        }
      }
    } catch {
      flash('Failed to load orders', true);
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [authHeaders, statusFilter, flash, selectedOrder]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/orders/${orderId}`, { status: newStatus }, { headers: authHeaders() });
      flash(`Status updated to ${newStatus}`);
      loadOrders();
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, status: newStatus }));
    } catch {
      flash('Failed to update status', true);
    }
  };

  const saveNotes = async (orderId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/orders/${orderId}`, { notes: editNotes }, { headers: authHeaders() });
      flash('Notes saved');
      loadOrders();
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, notes: editNotes }));
    } catch {
      flash('Failed to save notes', true);
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/admin/orders/${orderId}`, { headers: authHeaders() });
      flash('Deleted successfully');
      if (selectedOrder?.id === orderId) setSelectedOrder(null);
      setSelectedIds(prev => { const s = new Set(prev); s.delete(orderId); return s; });
      loadOrders();
    } catch {
      flash('Failed to delete', true);
    }
  };

  const updatePriority = async (orderId, priority) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/orders/${orderId}`, { priority }, { headers: authHeaders() });
      flash(`Priority set to ${priority}`);
      loadOrders();
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, priority }));
    } catch {
      flash('Failed to update priority', true);
    }
  };

  const updateFollowUp = async (orderId, follow_up_date) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/orders/${orderId}`, { follow_up_date }, { headers: authHeaders() });
      flash('Follow-up date saved');
      loadOrders();
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, follow_up_date }));
    } catch {
      flash('Failed to update follow-up date', true);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(o => o.id)));
    }
  };

  const filtered = orders
    .filter(o => {
      if (typeFilter !== 'all' && o.type !== typeFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return o.customer_name?.toLowerCase().includes(q) ||
        o.customer_email?.toLowerCase().includes(q) ||
        o.product?.toLowerCase().includes(q) ||
        o.id?.toLowerCase().includes(q) ||
        getTypeConfig(o.type).label.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'date') {
        cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
      } else if (sortField === 'name') {
        cmp = (a.customer_name || '').localeCompare(b.customer_name || '');
      } else if (sortField === 'type') {
        cmp = (a.type || '').localeCompare(b.type || '');
      } else if (sortField === 'status') {
        const si = (s) => STATUSES.findIndex(st => st.id === s);
        cmp = si(a.status) - si(b.status);
      } else if (sortField === 'priority') {
        const pi = (p) => { const idx = PRIORITIES.findIndex(pr => pr.id === p); return idx === -1 ? 99 : idx; };
        cmp = pi(a.priority) - pi(b.priority);
      } else if (sortField === 'followup') {
        cmp = (a.follow_up_date || '9999').localeCompare(b.follow_up_date || '9999');
      }
      return sortOrder === 'desc' ? -cmp : cmp;
    });

  const statusCounts = STATUSES.reduce((acc, s) => {
    acc[s.id] = orders.filter(o => o.status === s.id).length;
    return acc;
  }, {});

  const typeCounts = TYPES.reduce((acc, t) => {
    acc[t.id] = orders.filter(o => o.type === t.id).length;
    return acc;
  }, {});

  const selectedOrders = filtered.filter(o => selectedIds.has(o.id));

  return (
    <div className="p-4 sm:p-8" data-testid="order-management-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900" data-testid="tab-title">Orders & Contacts</h2>
          <p className="text-sm text-gray-500 mt-1">{orders.length} total items</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => { setRefreshing(true); await loadOrders(); setRefreshing(false); }}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            title="Refresh orders"
            data-testid="refresh-orders-btn"
          >
            <RefreshCw size={16} className={`text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <ShareExportMenu
          onExportCSV={() => exportCSV(selectedIds.size > 0 ? selectedOrders : filtered)}
          onExportPDF={() => exportPDF(selectedIds.size > 0 ? selectedOrders : filtered)}
          shareTitle="Total Composites — Orders & Contacts"
          selectedCount={selectedIds.size}
        />
        </div>
      </div>

      {/* Last updated */}
      <p className="text-xs text-gray-400 mb-4" data-testid="orders-last-updated">
        Last updated: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </p>

      {/* Type Filter Pills */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="type-filters">
        <button
          onClick={() => setTypeFilter('all')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            typeFilter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="type-filter-all"
        >
          All ({orders.length})
        </button>
        {TYPES.map(t => {
          const Icon = t.icon;
          const active = typeFilter === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTypeFilter(active ? 'all' : t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                active ? 'shadow-sm' : 'hover:opacity-80'
              }`}
              style={active ? { backgroundColor: t.color, color: '#fff' } : { backgroundColor: t.bg, color: t.color }}
              data-testid={`type-filter-${t.id}`}
            >
              <Icon size={12} />
              {t.label} ({typeCounts[t.id] || 0})
            </button>
          );
        })}
      </div>

      {/* Status Pipeline */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 sm:gap-3 mb-4" data-testid="status-pipeline">
        {STATUSES.map(s => {
          const Icon = STATUS_ICONS[s.id];
          const active = statusFilter === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setStatusFilter(active ? 'all' : s.id)}
              className={`flex items-center gap-1.5 px-2.5 py-2 sm:px-3 sm:py-2.5 rounded-xl border-2 transition-all ${
                active ? 'shadow-sm' : 'border-transparent hover:border-gray-200'
              }`}
              style={active ? { borderColor: s.color, backgroundColor: s.bg } : { backgroundColor: '#f9fafb' }}
              data-testid={`filter-${s.id}`}
            >
              <Icon size={12} style={{ color: s.color }} />
              <span className="text-[10px] sm:text-xs font-bold" style={{ color: active ? s.color : '#374151' }}>{s.label}</span>
              <span className="ml-auto text-[10px] sm:text-xs font-black" style={{ color: s.color }}>{statusCounts[s.id] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Search + Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:border-orange-300"
            placeholder="Search by name, email, product, type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="order-search"
          />
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <select
              value={sortField}
              onChange={e => setSortField(e.target.value)}
              className="appearance-none pl-8 pr-8 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 focus:outline-none focus:border-orange-300 bg-white"
              data-testid="sort-field"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="type">Sort by Type</option>
              <option value="status">Sort by Status</option>
              <option value="priority">Sort by Priority</option>
              <option value="followup">Sort by Follow-up</option>
            </select>
            <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <button
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            className="flex items-center gap-1.5 px-3 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:border-gray-300 transition-all"
            data-testid="sort-toggle"
          >
            {sortOrder === 'desc' ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
            {sortOrder === 'desc' ? 'DESC' : 'ASC'}
          </button>
        </div>
      </div>

      {/* Select All */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3 mb-3 px-1">
          <button
            onClick={toggleSelectAll}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
              selectedIds.size === filtered.length && filtered.length > 0 ? 'border-orange-500 bg-orange-500' : 'border-gray-300 hover:border-gray-400'
            }`}
            data-testid="select-all"
          >
            {selectedIds.size === filtered.length && filtered.length > 0 && <Check size={10} className="text-white" />}
          </button>
          <span className="text-[11px] text-gray-400">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : `Select all (${filtered.length})`}
          </span>
        </div>
      )}

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" data-testid="no-orders">
          <Package size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-bold text-gray-400">
            {orders.length === 0 ? 'No orders or contacts yet' : 'No items match your filter'}
          </p>
          <p className="text-xs text-gray-300 mt-1">Items appear here when customers submit order or contact forms</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="orders-list">
          {filtered.map(order => {
            const status = STATUSES.find(s => s.id === order.status) || STATUSES[0];
            const typeConf = getTypeConfig(order.type);
            const TypeIcon = typeConf.icon;
            const isSelected = selectedOrder?.id === order.id;
            const isNew = order.status === 'new';
            const isChecked = selectedIds.has(order.id);
            return (
              <div
                key={order.id}
                className={`rounded-xl border overflow-hidden transition-all ${
                  isNew
                    ? 'bg-white border-red-200 ring-1 ring-red-100'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`order-row-${order.id}`}
              >
                {/* Order Row */}
                <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3">
                  {/* Checkbox */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(order.id); }}
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                      isChecked ? 'border-orange-500 bg-orange-500' : 'border-gray-300 hover:border-gray-400'
                    }`}
                    data-testid={`checkbox-${order.id}`}
                  >
                    {isChecked && <Check size={10} className="text-white" />}
                  </button>

                  {/* Type Icon */}
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: typeConf.bg }}
                    title={typeConf.label}
                    data-testid={`type-icon-${order.type}`}
                  >
                    <TypeIcon size={14} style={{ color: typeConf.color }} />
                  </div>

                  {/* Customer Info — clickable to expand */}
                  <button
                    onClick={() => {
                      if (isSelected) { setSelectedOrder(null); } 
                      else { setSelectedOrder(order); setEditNotes(order.notes || ''); }
                    }}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm truncate ${isNew ? 'font-black text-gray-900' : 'font-bold text-gray-800'}`}>
                        {order.customer_name || 'Unknown'}
                      </span>
                      {isNew && (
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-red-500 text-white uppercase tracking-wider" data-testid="new-badge">
                          New
                        </span>
                      )}
                      {(() => {
                        const pr = getPriorityConfig(order.priority);
                        if (!pr) return null;
                        const PrIcon = pr.icon;
                        return (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: pr.bg, color: pr.color }}
                            data-testid={`priority-badge-${order.id}`}
                          >
                            {PrIcon && <PrIcon size={8} />} {pr.label}
                          </span>
                        );
                      })()}
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full hidden sm:inline" style={{ backgroundColor: status.bg, color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-400 truncate">{order.customer_email}</span>
                      {order.product && <span className="text-xs font-semibold text-gray-600 hidden sm:inline">{order.product}</span>}
                      {order.follow_up_date && (() => {
                        const isOverdue = new Date(order.follow_up_date) < new Date();
                        const isSoon = !isOverdue && new Date(order.follow_up_date) < new Date(Date.now() + 3 * 86400000);
                        return (
                          <span className={`hidden sm:inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            isOverdue ? 'bg-red-50 text-red-600' : isSoon ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'
                          }`} data-testid={`followup-indicator-${order.id}`}>
                            <CalendarClock size={8} />
                            {isOverdue ? 'Overdue' : isSoon ? 'Due soon' : formatDate(order.follow_up_date)}
                          </span>
                        );
                      })()}
                    </div>
                  </button>

                  {/* Type + Date */}
                  <div className="hidden md:flex flex-col items-end flex-shrink-0">
                    <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: typeConf.color }}>
                      {typeConf.label}
                    </span>
                    <span className="text-xs text-gray-400 mt-0.5">{formatDate(order.created_at)}</span>
                  </div>

                  <ArrowRight size={14} className={`text-gray-300 transition-transform flex-shrink-0 ${isSelected ? 'rotate-90' : ''}`} />
                </div>

                {/* Expanded Detail */}
                {isSelected && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-4" data-testid="order-detail">
                    {/* Type badge in detail */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: typeConf.bg }}>
                        <TypeIcon size={12} style={{ color: typeConf.color }} />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: typeConf.color }}>{typeConf.label}</span>
                      <span className="text-xs text-gray-400">· {order.source_page || 'website'}</span>
                      {/* Single-item export */}
                      <button
                        onClick={() => exportPDF([order])}
                        className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-gray-500 border border-gray-200 rounded hover:bg-white transition-all"
                        data-testid="export-single-pdf"
                      >
                        <Download size={10} /> PDF
                      </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2 space-y-4">
                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Customer Details</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                              <Mail size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700 truncate">{order.customer_email || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{order.customer_phone || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Building2 size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{order.customer_company || '—'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock size={12} className="text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{formatDate(order.created_at)} {formatTime(order.created_at)}</span>
                            </div>
                          </div>
                        </div>

                        {order.details && Object.keys(order.details).length > 0 && (
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Details</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {Object.entries(order.details).map(([key, val]) => (
                                <div key={key}>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase">{key.replace(/_/g, ' ')}</p>
                                  <p className="text-sm text-gray-800 font-medium break-words">{String(val)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {order.message && (
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Message</h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.message}</p>
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Update Status</h4>
                          <div className="space-y-1.5">
                            {STATUSES.map(s => {
                              const Icon = STATUS_ICONS[s.id];
                              const isCurrent = order.status === s.id;
                              return (
                                <button
                                  key={s.id}
                                  onClick={() => updateStatus(order.id, s.id)}
                                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                                    isCurrent ? 'ring-2' : 'hover:bg-gray-50'
                                  }`}
                                  style={isCurrent ? { backgroundColor: s.bg, color: s.color, ringColor: s.color } : { color: '#6b7280' }}
                                  data-testid={`status-btn-${s.id}`}
                                >
                                  <Icon size={13} />
                                  <span>{s.label}</span>
                                  {isCurrent && <Check size={12} className="ml-auto" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Priority */}
                        <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="priority-section">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Priority</h4>
                          <div className="grid grid-cols-2 gap-1.5">
                            {PRIORITIES.map(pr => {
                              const isCurrent = order.priority === pr.id;
                              const PrIcon = pr.icon;
                              return (
                                <button
                                  key={pr.id}
                                  onClick={() => updatePriority(order.id, isCurrent ? null : pr.id)}
                                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                                    isCurrent ? 'ring-2 shadow-sm' : 'hover:opacity-80'
                                  }`}
                                  style={isCurrent
                                    ? { backgroundColor: pr.bg, color: pr.color, '--tw-ring-color': pr.color }
                                    : { backgroundColor: '#f9fafb', color: '#6b7280' }
                                  }
                                  data-testid={`priority-btn-${pr.id}`}
                                >
                                  {PrIcon && <PrIcon size={12} />}
                                  {pr.label}
                                  {isCurrent && <Check size={10} className="ml-auto" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Follow-up Date */}
                        <div className="bg-white rounded-xl p-4 border border-gray-100" data-testid="followup-section">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Follow-up Date</h4>
                          <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-300"
                            value={order.follow_up_date || ''}
                            onChange={e => updateFollowUp(order.id, e.target.value || null)}
                            data-testid="followup-date-input"
                          />
                          {order.follow_up_date && (() => {
                            const isOverdue = new Date(order.follow_up_date) < new Date();
                            const isSoon = !isOverdue && new Date(order.follow_up_date) < new Date(Date.now() + 3 * 86400000);
                            return (
                              <div className={`mt-2 flex items-center gap-1.5 text-xs font-bold ${
                                isOverdue ? 'text-red-600' : isSoon ? 'text-amber-600' : 'text-blue-600'
                              }`}>
                                <CalendarClock size={12} />
                                {isOverdue ? 'Overdue!' : isSoon ? 'Due within 3 days' : `Due ${formatDate(order.follow_up_date)}`}
                              </div>
                            );
                          })()}
                          {order.follow_up_date && (
                            <button
                              onClick={() => updateFollowUp(order.id, null)}
                              className="mt-2 text-[10px] font-bold text-gray-400 hover:text-red-500 transition-colors"
                              data-testid="clear-followup-btn"
                            >
                              Clear date
                            </button>
                          )}
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Notes</h4>
                          <textarea
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-300 resize-none"
                            rows={4}
                            placeholder="Add internal notes..."
                            value={editNotes}
                            onChange={e => setEditNotes(e.target.value)}
                            data-testid="order-notes"
                          />
                          <button
                            onClick={() => saveNotes(order.id)}
                            className="mt-2 w-full px-3 py-2 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90"
                            style={{ backgroundColor: '#E8520A' }}
                            data-testid="save-notes-btn"
                          >
                            Save Notes
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedOrder(null)}
                            className="flex-1 px-3 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                          >
                            <X size={12} className="inline mr-1" /> Close
                          </button>
                          <button
                            onClick={() => deleteOrder(order.id)}
                            className="px-3 py-2 text-xs font-bold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-all"
                            data-testid="delete-order-btn"
                          >
                            <Trash2 size={12} className="inline mr-1" /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderManagementTab;
